/**
 * title 속성 탈착·복원 유틸 (GlobalTooltip 전용).
 *
 * 브라우저 기본 툴팁의 1~2초 지연은 CSS·JS로 조절할 수 없다. 빠른 커스텀 툴팁을 띄우려면
 * hover 동안 `title` 속성을 DOM에서 잠시 떼는 것 외에 네이티브 툴팁을 막을 방법이 없다.
 *
 * 문제는 React가 props를 다시 계산하지 않는 한 지워진 title을 복구해주지 않는다는 점이다.
 * 복원에 실패하면 그 버튼의 툴팁이 영구히 사라지므로, 이 기능에서 유일하게 되돌릴 수 없는
 * 손상을 낼 수 있는 코드를 이 파일 하나에 격리했다.
 *
 * 불변식: "title이 떼인 요소는 항상 0개 또는 1개이고, 원본은 data-tt-title에 있다."
 * 콘솔에서 `document.querySelectorAll('[data-tt-title]').length`로 즉시 검증할 수 있다.
 */

/** 원본 title 백업 위치. React가 관리하지 않는 속성이라 리렌더와 충돌하지 않는다 */
const ATTR = 'data-tt-title';
/** aria-label을 우리가 임시로 붙였다는 표시 (남의 aria-label을 지우지 않기 위해) */
const ARIA = 'data-tt-aria';

/**
 * 툴팁 대상 셀렉터.
 * title이 제거된 뒤에도 같은 요소가 다시 잡히도록 백업 속성(ATTR)을 포함한다.
 * data-tooltip은 title을 쓸 수 없는 곳(disabled 버튼 래퍼 등)을 위한 옵트인.
 */
export const TIP_SELECTOR = `[title]:not([title=""]), [${ATTR}], [data-tooltip]`;

/**
 * 요소의 툴팁 텍스트를 읽고, title이 살아있으면 백업 후 제거한다(네이티브 툴팁 억제).
 * 텍스트가 없으면 빈 문자열을 반환하고 아무것도 건드리지 않는다.
 */
export function captureTitle(el: HTMLElement): string {
  const text = (
    el.getAttribute('title') ||
    el.getAttribute(ATTR) ||
    el.getAttribute('data-tooltip') ||
    ''
  ).trim();
  if (!text) return '';

  if (el.hasAttribute('title')) {
    el.setAttribute(ATTR, text);
    el.removeAttribute('title');

    // 아이콘 버튼은 title이 유일한 접근 가능한 이름인 경우가 많다.
    // title을 떼는 동안 이름이 비지 않도록 같은 문자열을 aria-label로 임시 대여한다.
    const hasOwnName =
      el.hasAttribute('aria-label') ||
      el.hasAttribute('aria-labelledby') ||
      !!(el.textContent || '').trim();
    if (!hasOwnName) {
      el.setAttribute('aria-label', text);
      el.setAttribute(ARIA, '');
    }
  }
  return text;
}

/**
 * 백업해둔 title을 되돌린다. 여러 번 호출해도 안전(멱등).
 */
export function restoreTitle(el: HTMLElement): void {
  const saved = el.getAttribute(ATTR);
  if (saved === null) return;

  el.removeAttribute(ATTR);
  if (el.hasAttribute(ARIA)) {
    el.removeAttribute('aria-label');
    el.removeAttribute(ARIA);
  }

  // 그 사이 React가 새 title을 써넣었다면(리액션 명단·파일명 등 동적 title)
  // 오래된 값으로 덮어쓰면 안 된다.
  if (!el.hasAttribute('title')) el.setAttribute('title', saved);
}

/**
 * 최후의 안전망 — 어떤 경로로든 복원되지 못한 백업을 전부 회수한다.
 * 대상은 정상적으로 최대 1개이므로 비용은 무시할 수준이다.
 */
export function restoreAllTitles(): void {
  const stripped = document.querySelectorAll<HTMLElement>(`[${ATTR}]`);
  stripped.forEach(restoreTitle);
}
